import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import useAuthStore from '../hooks/useAuthStore';

const streams = ['ICSE Class 9', 'ICSE Class 10', 'ISC Class 11', 'ISC Class 12'];

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout, updateProfile } = useAuthStore();
  
  const [username, setUsername] = useState(user?.name || '');
  const [stream, setStream] = useState(user?.stream || streams[0]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    setMessage({ type: '', text: '' });
  };

  const handleStreamChange = (e) => {
    setStream(e.target.value);
    setMessage({ type: '', text: '' });
  };

  const validateUsername = (name) => {
    if (!name.trim()) {
      return 'Username cannot be empty';
    }
    if (name.length < 3) {
      return 'Username must be at least 3 characters';
    }
    if (name.length > 30) {
      return 'Username must be less than 30 characters';
    }
    if (!/^[a-zA-Z\s]+$/.test(name)) {
      return 'Username can only contain letters and spaces';
    }
    return null;
  };

  const handleUpdateProfile = async () => {
    const validationError = validateUsername(username);
    if (validationError) {
      setMessage({ type: 'error', text: validationError });
      return;
    }

    setIsUpdating(true);
    setMessage({ type: '', text: '' });

    const result = await updateProfile(username, stream);
    
    setIsUpdating(false);

    if (result.success) {
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to update profile' });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen w-full text-white antialiased bg-[linear-gradient(180deg,_#0d0b18_0%,_#16122c_50%,_#0b0914_100%)] relative overflow-x-hidden">
      {/* Soft, silk-like velvet glow overlay */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px] pointer-events-none" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 text-white hover:text-purple-400 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="font-medium">Back to Dashboard</span>
          </button>

          <h1 className="text-2xl font-bold text-white">Profile Settings</h1>

          <div className="w-24" /> {/* Spacer for centering */}
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass-panel rounded-3xl p-8 border border-white/10"
          >
            <h2 className="text-3xl font-bold text-white mb-2">Account Settings</h2>
            <p className="text-gray-400 mb-8">Manage your profile information and preferences</p>

            {/* Username Section */}
            <div className="mb-8">
              <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={handleUsernameChange}
                placeholder="Enter your username"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                maxLength={30}
              />
              <p className="text-xs text-gray-500 mt-1">
                3-30 characters, letters and spaces only
              </p>
            </div>

            {/* Class/Stream Section */}
            <div className="mb-8">
              <label htmlFor="stream" className="block text-sm font-medium text-gray-300 mb-2">
                Class / Batch
              </label>
              <select
                id="stream"
                value={stream}
                onChange={handleStreamChange}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all cursor-pointer"
              >
                {streams.map((s) => (
                  <option key={s} value={s} className="bg-gray-900">
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {/* Email Display (Read-only) */}
            <div className="mb-8">
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={user?.email || ''}
                readOnly
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-gray-400 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                Email cannot be changed
              </p>
            </div>

            {/* Message Display */}
            {message.text && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mb-6 p-4 rounded-xl ${
                  message.type === 'success' 
                    ? 'bg-green-500/10 border border-green-500/20 text-green-400' 
                    : 'bg-red-500/10 border border-red-500/20 text-red-400'
                }`}
              >
                <div className="flex items-center gap-2">
                  {message.type === 'success' ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <span className="text-sm font-medium">{message.text}</span>
                </div>
              </motion.div>
            )}

            {/* Update Button */}
            <button
              onClick={handleUpdateProfile}
              disabled={isUpdating}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-medium rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-purple-500/25"
            >
              {isUpdating ? 'Updating...' : 'Update Profile'}
            </button>

            {/* Divider */}
            <div className="my-8 border-t border-white/10" />

            {/* Logout Section */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Session</h3>
              <button
                onClick={handleLogout}
                className="w-full px-6 py-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 font-medium rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-red-500/25"
              >
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Logout</span>
                </div>
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                You will be redirected to the login page
              </p>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
